/**
 * Generates pixel art sprites at runtime using canvas
 * Creates a Commander Keen inspired art style
 */

// Color palettes (EGA-inspired)
const PALETTE = {
  // Player
  playerSkin: '#FFD8B0',
  playerHair: '#8B4513',
  playerShirt: '#FF0000',
  playerPants: '#0000FF',
  playerOutline: '#000000',

  // Enemies
  sluggerBody: '#00AA00',
  sluggerEye: '#FFFFFF',
  hopperBody: '#AA00AA',
  hopperEye: '#FFFF00',
  shooterBody: '#AA0000',
  shooterGun: '#555555',

  // Tiles
  groundTop: '#55AA55',
  groundMid: '#8B4513',
  groundDark: '#654321',
  metalLight: '#AAAAAA',
  metalDark: '#555555',

  // Items
  coinGold: '#FFD700',
  coinShine: '#FFFFAA',
  keyYellow: '#FFFF00',
  keyRed: '#FF0000',
  keyBlue: '#0088FF',
  floppyBody: '#333333',
  floppyLabel: '#00AAFF',
  ammoGreen: '#00FF00',

  // Effects
  transparent: 'rgba(0,0,0,0)',
};

export interface GeneratedSprites {
  player: HTMLCanvasElement;
  playerRun1: HTMLCanvasElement;
  playerRun2: HTMLCanvasElement;
  playerJump: HTMLCanvasElement;
  slugger: HTMLCanvasElement;
  slugger2: HTMLCanvasElement;
  hopper: HTMLCanvasElement;
  hopper2: HTMLCanvasElement;
  shooter: HTMLCanvasElement;
  tileGround: HTMLCanvasElement;
  tilePlatform: HTMLCanvasElement;
  tileWall: HTMLCanvasElement;
  coin: HTMLCanvasElement;
  floppy: HTMLCanvasElement;
  keyYellow: HTMLCanvasElement;
  keyRed: HTMLCanvasElement;
  keyBlue: HTMLCanvasElement;
  ammo: HTMLCanvasElement;
  doorClosed: HTMLCanvasElement;
  doorOpen: HTMLCanvasElement;
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function drawPixel(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, 1, 1);
}

function drawRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

// Player sprite (16x16)
function generatePlayer(frame: number = 0): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  // Clear
  ctx.clearRect(0, 0, 16, 16);

  const legOffset = frame === 1 ? 1 : frame === 2 ? -1 : 0;
  const armOffset = frame === 1 ? -1 : frame === 2 ? 1 : 0;
  const isJump = frame === 3;

  // Head (helmet)
  drawRect(ctx, 5, 1, 6, 5, '#FF0000'); // Red helmet
  drawRect(ctx, 6, 2, 4, 3, '#00AAFF'); // Visor
  drawRect(ctx, 7, 3, 2, 1, '#FFFFFF'); // Visor shine

  // Face
  drawRect(ctx, 6, 5, 4, 2, PALETTE.playerSkin);

  // Body (shirt)
  drawRect(ctx, 5, 7, 6, 4, '#FF0000');
  drawRect(ctx, 6, 8, 4, 2, '#CC0000'); // Shade

  // Arms
  if (isJump) {
    drawRect(ctx, 3, 7, 2, 3, PALETTE.playerSkin); // Left arm up
    drawRect(ctx, 11, 7, 2, 3, PALETTE.playerSkin); // Right arm up
  } else {
    drawRect(ctx, 3, 8 + armOffset, 2, 3, PALETTE.playerSkin);
    drawRect(ctx, 11, 8 - armOffset, 2, 3, PALETTE.playerSkin);
  }

  // Legs
  if (isJump) {
    drawRect(ctx, 5, 11, 2, 4, PALETTE.playerPants);
    drawRect(ctx, 9, 11, 2, 4, PALETTE.playerPants);
  } else {
    drawRect(ctx, 5, 11, 2, 4 + legOffset, PALETTE.playerPants);
    drawRect(ctx, 9, 11, 2, 4 - legOffset, PALETTE.playerPants);
  }

  // Feet
  drawRect(ctx, 4, 14 + (isJump ? 0 : legOffset), 3, 2, '#8B4513');
  drawRect(ctx, 9, 14 + (isJump ? 0 : -legOffset), 3, 2, '#8B4513');

  return canvas;
}

// Slugger enemy (16x16) - basic patrol enemy
function generateSlugger(frame: number = 0): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  const bounce = frame === 1 ? -1 : 0;

  // Body (slug shape)
  drawRect(ctx, 2, 8 + bounce, 12, 6, PALETTE.sluggerBody);
  drawRect(ctx, 4, 6 + bounce, 8, 2, PALETTE.sluggerBody);

  // Slime trail
  drawRect(ctx, 3, 14, 10, 2, '#55DD55');

  // Eyes
  drawRect(ctx, 4, 7 + bounce, 3, 3, PALETTE.sluggerEye);
  drawRect(ctx, 9, 7 + bounce, 3, 3, PALETTE.sluggerEye);
  drawPixel(ctx, 5, 8 + bounce, '#000000');
  drawPixel(ctx, 10, 8 + bounce, '#000000');

  // Antennae
  drawRect(ctx, 5, 4 + bounce, 1, 3, PALETTE.sluggerBody);
  drawRect(ctx, 10, 4 + bounce, 1, 3, PALETTE.sluggerBody);
  drawPixel(ctx, 5, 3 + bounce, '#FFFF00');
  drawPixel(ctx, 10, 3 + bounce, '#FFFF00');

  return canvas;
}

// Hopper enemy (16x16) - jumping enemy
function generateHopper(frame: number = 0): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  const squish = frame === 1;

  // Body
  if (squish) {
    drawRect(ctx, 3, 10, 10, 5, PALETTE.hopperBody);
  } else {
    drawRect(ctx, 4, 6, 8, 8, PALETTE.hopperBody);
  }

  // Eyes (big)
  const eyeY = squish ? 10 : 6;
  drawRect(ctx, 4, eyeY, 4, 4, PALETTE.hopperEye);
  drawRect(ctx, 8, eyeY, 4, 4, PALETTE.hopperEye);
  drawRect(ctx, 5, eyeY + 1, 2, 2, '#000000');
  drawRect(ctx, 9, eyeY + 1, 2, 2, '#000000');

  // Legs (springs)
  if (squish) {
    drawRect(ctx, 4, 15, 3, 1, '#888888');
    drawRect(ctx, 9, 15, 3, 1, '#888888');
  } else {
    drawRect(ctx, 4, 14, 2, 2, '#888888');
    drawRect(ctx, 10, 14, 2, 2, '#888888');
    drawRect(ctx, 5, 12, 1, 2, '#888888');
    drawRect(ctx, 10, 12, 1, 2, '#888888');
  }

  return canvas;
}

// Shooter enemy (16x16)
function generateShooter(): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  // Body (robot)
  drawRect(ctx, 4, 4, 8, 8, PALETTE.shooterBody);
  drawRect(ctx, 5, 5, 6, 6, '#880000');

  // Eye (single)
  drawRect(ctx, 6, 6, 4, 3, '#FFFF00');
  drawRect(ctx, 7, 7, 2, 1, '#FF0000');

  // Gun arm
  drawRect(ctx, 12, 6, 4, 3, PALETTE.shooterGun);
  drawRect(ctx, 14, 7, 2, 1, '#FFFF00');

  // Legs
  drawRect(ctx, 5, 12, 2, 4, PALETTE.shooterGun);
  drawRect(ctx, 9, 12, 2, 4, PALETTE.shooterGun);

  // Antenna
  drawRect(ctx, 7, 1, 2, 3, PALETTE.shooterGun);
  drawRect(ctx, 6, 0, 4, 2, '#FF0000');

  return canvas;
}

// Ground tile (16x16)
function generateTileGround(): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  // Base
  drawRect(ctx, 0, 0, 16, 16, PALETTE.groundMid);

  // Top grass
  drawRect(ctx, 0, 0, 16, 4, PALETTE.groundTop);

  // Grass tufts
  for (let x = 0; x < 16; x += 4) {
    drawPixel(ctx, x + 1, 0, '#77CC77');
    drawPixel(ctx, x + 2, 0, '#77CC77');
  }

  // Dirt texture
  drawPixel(ctx, 2, 6, PALETTE.groundDark);
  drawPixel(ctx, 8, 8, PALETTE.groundDark);
  drawPixel(ctx, 12, 10, PALETTE.groundDark);
  drawPixel(ctx, 5, 12, PALETTE.groundDark);
  drawPixel(ctx, 14, 14, PALETTE.groundDark);

  // Highlights
  drawPixel(ctx, 4, 7, '#A67B5B');
  drawPixel(ctx, 10, 11, '#A67B5B');

  return canvas;
}

// Platform tile (16x16)
function generateTilePlatform(): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  // Metal platform
  drawRect(ctx, 0, 0, 16, 16, PALETTE.metalDark);
  drawRect(ctx, 0, 0, 16, 4, PALETTE.metalLight);

  // Rivets
  drawRect(ctx, 2, 1, 2, 2, '#DDDDDD');
  drawRect(ctx, 12, 1, 2, 2, '#DDDDDD');

  // Panel lines
  drawRect(ctx, 0, 4, 16, 1, '#333333');
  drawRect(ctx, 7, 5, 2, 11, '#333333');

  // Highlights
  drawRect(ctx, 1, 5, 5, 1, '#777777');
  drawRect(ctx, 10, 5, 5, 1, '#777777');

  return canvas;
}

// Wall tile (16x16)
function generateTileWall(): HTMLCanvasElement {
  const canvas = createCanvas(16, 16);
  const ctx = canvas.getContext('2d')!;

  // Brick pattern
  drawRect(ctx, 0, 0, 16, 16, '#884422');

  // Mortar lines
  drawRect(ctx, 0, 7, 16, 2, '#666666');
  drawRect(ctx, 0, 15, 16, 1, '#666666');
  drawRect(ctx, 7, 0, 2, 7, '#666666');
  drawRect(ctx, 0, 8, 2, 7, '#666666');
  drawRect(ctx, 14, 8, 2, 7, '#666666');

  // Brick highlights
  drawRect(ctx, 1, 1, 5, 1, '#AA6644');
  drawRect(ctx, 9, 1, 5, 1, '#AA6644');
  drawRect(ctx, 3, 9, 5, 1, '#AA6644');
  drawRect(ctx, 10, 9, 3, 1, '#AA6644');

  return canvas;
}

// Coin (12x12)
function generateCoin(): HTMLCanvasElement {
  const canvas = createCanvas(12, 12);
  const ctx = canvas.getContext('2d')!;

  // Coin body
  drawRect(ctx, 2, 1, 8, 10, PALETTE.coinGold);
  drawRect(ctx, 1, 2, 10, 8, PALETTE.coinGold);

  // Shine
  drawRect(ctx, 3, 2, 2, 3, PALETTE.coinShine);

  // Shadow
  drawRect(ctx, 7, 7, 2, 2, '#CC9900');

  // Dollar sign or star
  drawPixel(ctx, 5, 4, '#CC9900');
  drawPixel(ctx, 6, 4, '#CC9900');
  drawPixel(ctx, 5, 5, '#CC9900');
  drawPixel(ctx, 6, 6, '#CC9900');
  drawPixel(ctx, 5, 7, '#CC9900');
  drawPixel(ctx, 6, 7, '#CC9900');

  return canvas;
}

// Floppy disk (14x14)
function generateFloppy(): HTMLCanvasElement {
  const canvas = createCanvas(14, 14);
  const ctx = canvas.getContext('2d')!;

  // Disk body
  drawRect(ctx, 1, 1, 12, 12, PALETTE.floppyBody);

  // Metal slider
  drawRect(ctx, 3, 0, 8, 4, '#888888');
  drawRect(ctx, 5, 1, 4, 2, '#333333');

  // Label
  drawRect(ctx, 2, 5, 10, 6, PALETTE.floppyLabel);
  drawRect(ctx, 3, 6, 8, 4, '#0088AA');

  // Label text lines
  drawRect(ctx, 4, 7, 6, 1, '#FFFFFF');
  drawRect(ctx, 4, 9, 4, 1, '#FFFFFF');

  // Corner notch
  drawRect(ctx, 11, 11, 2, 2, '#222222');

  return canvas;
}

// Key (12x12)
function generateKey(color: string): HTMLCanvasElement {
  const canvas = createCanvas(12, 12);
  const ctx = canvas.getContext('2d')!;

  // Key head (circle)
  drawRect(ctx, 1, 1, 6, 6, color);
  drawRect(ctx, 0, 2, 8, 4, color);
  drawRect(ctx, 2, 0, 4, 8, color);

  // Hole in head
  drawRect(ctx, 3, 3, 2, 2, '#333333');

  // Key shaft
  drawRect(ctx, 6, 4, 5, 2, color);

  // Key teeth
  drawRect(ctx, 9, 6, 2, 2, color);
  drawRect(ctx, 7, 6, 1, 1, color);

  // Shine
  drawPixel(ctx, 2, 2, '#FFFFFF');

  return canvas;
}

// Ammo (10x10)
function generateAmmo(): HTMLCanvasElement {
  const canvas = createCanvas(10, 10);
  const ctx = canvas.getContext('2d')!;

  // Ammo pack
  drawRect(ctx, 1, 2, 8, 6, PALETTE.ammoGreen);
  drawRect(ctx, 2, 1, 6, 8, PALETTE.ammoGreen);

  // Lightning bolt
  drawRect(ctx, 4, 2, 2, 2, '#FFFF00');
  drawRect(ctx, 3, 4, 2, 2, '#FFFF00');
  drawRect(ctx, 4, 6, 2, 2, '#FFFF00');

  return canvas;
}

// Door (16x32)
function generateDoor(open: boolean): HTMLCanvasElement {
  const canvas = createCanvas(16, 32);
  const ctx = canvas.getContext('2d')!;

  // Frame
  drawRect(ctx, 0, 0, 16, 32, '#444444');
  drawRect(ctx, 2, 2, 12, 28, '#333333');

  if (open) {
    // Open door (dark inside)
    drawRect(ctx, 3, 3, 10, 26, '#111111');
  } else {
    // Closed door
    drawRect(ctx, 3, 3, 10, 26, '#8B4513');

    // Panels
    drawRect(ctx, 4, 4, 8, 10, '#A0522D');
    drawRect(ctx, 4, 16, 8, 10, '#A0522D');

    // Handle
    drawRect(ctx, 10, 14, 2, 4, '#FFD700');
  }

  return canvas;
}

/**
 * Generate all sprites
 */
export function generateAllSprites(): GeneratedSprites {
  return {
    player: generatePlayer(0),
    playerRun1: generatePlayer(1),
    playerRun2: generatePlayer(2),
    playerJump: generatePlayer(3),
    slugger: generateSlugger(0),
    slugger2: generateSlugger(1),
    hopper: generateHopper(0),
    hopper2: generateHopper(1),
    shooter: generateShooter(),
    tileGround: generateTileGround(),
    tilePlatform: generateTilePlatform(),
    tileWall: generateTileWall(),
    coin: generateCoin(),
    floppy: generateFloppy(),
    keyYellow: generateKey(PALETTE.keyYellow),
    keyRed: generateKey(PALETTE.keyRed),
    keyBlue: generateKey(PALETTE.keyBlue),
    ammo: generateAmmo(),
    doorClosed: generateDoor(false),
    doorOpen: generateDoor(true),
  };
}

/**
 * Convert canvas to base64 data URL
 */
export function canvasToDataURL(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/png');
}
